import * as constants from "@client/constants";
import * as dates from "@client/dates";
import * as forms from "@client/forms";
import {Label, SelectNumber} from "@client/forms";
import * as typography from "@client/shared/typography";
import * as utils from "@client/utils";
import {server} from "@reactivated";
import {Decimal} from "decimal.js";
import React from "react";

type Profile = server.journal.nutrition.CalculatorProfile.output;

const getBMR = (profile: Profile) => {
    if (
        profile.weight != null &&
        profile.height != null &&
        profile.date_of_birth != null
    ) {
        const age = dates.differenceInYears(
            new Date(),
            dates.parseISO(profile.date_of_birth),
        );

        const weight = parseFloat(profile.weight); // || '81');
        const height = parseFloat(profile.height); //  || '182');

        // Use Katch-McArdle formula when body fat is provided.
        if (profile.body_fat != null) {
            const bodyFat = parseFloat(profile.body_fat);
            const lbm = ((100 - bodyFat) * weight) / 100;
            return 370 + 21.6 * lbm;
        }
        // Otherwise use Mifflin St Jeor formula.
        else {
            const s = profile.gender === "m" ? 5 : -161;
            return 10 * weight + 6.25 * height - 5 * age + s;
        }
    }
    return 0;
};

const getTDEE = (profile: Profile) => {
    const activity = parseFloat(profile.activity ?? "");
    return getBMR(profile) * activity;
};

const round = (input: number) => {
    if (Number.isNaN(input)) {
        return "N/A";
    }
    return Math.round(input).toString();
};

interface CalculatorResult {
    name: string;
    calories: number;
    protein: number;
    fat: number;
    carbs: number;
    useNetCarbs?: boolean;
}

interface Diet<T extends {[K in keyof T]: forms.FieldDefinition<unknown>}> {
    presets: (forms.ValueFields<forms.FormDefinition<T>> & {name: string})[];
    results: ((input: {
        settings: forms.ValueFields<forms.FormDefinition<T>>;
        profile: Profile;
    }) => CalculatorResult)[];
    assignments?: number[];
}

export const getRoundedResults = (input: CalculatorResult, useKilojoules: boolean) => {
    const energy = useKilojoules
        ? input.calories / parseFloat(constants.KILOJOULES_MULTIPLIER)
        : input.calories;

    return {
        ...input,
        calories_target: round(energy),
        calories_nearest: utils
            .roundNumberToNearest(new Decimal(energy), new Decimal(50))
            .toString(),
        protein_target: round(input.protein),
        protein_nearest: utils
            .roundNumberToNearest(new Decimal(input.protein), new Decimal(5))
            .toString(),
        fat_target: round(input.fat),
        fat_nearest: utils
            .roundNumberToNearest(new Decimal(input.fat), new Decimal(5))
            .toString(),
        carbs_target: round(input.carbs),
        carbs_nearest: utils
            .roundNumberToNearest(new Decimal(input.carbs), new Decimal(5))
            .toString(),
    };
};

interface DietResult {
    bmr: string | number;
    tdee: string | number;
    targets: Array<{
        name: string;
        calories_target: string;
        protein_target: string;
        fat_target: string;
        carbs_target: string;
    }>;
}

export const useCalculator = <
    const T extends {[K in keyof T]: forms.FieldDefinition<unknown>},
>({
    profile,
    formSchema,
    diet,
}: {
    profile: Profile;
    formSchema: forms.FormDefinition<T>;
    diet: Diet<T>;
}) => {
    const [selectedPreset, setSelectedPreset] = React.useState(0);
    const form = forms.useForm(formSchema, {initial: diet.presets[0]});

    const useKilojoules = profile.use_kilojoules;
    const validated = form.validated;
    const targets = validated.success
        ? diet.results.map((getResult) => {
              return getRoundedResults(
                  getResult({settings: validated.data, profile}),
                  useKilojoules,
              );
          })
        : [];

    const onPresetChange = (index: number | null) => {
        if (index != null) {
            setSelectedPreset(index);
            const preset = diet.presets[index];
            form.update(preset);
        }
    };

    React.useEffect(() => {
        onPresetChange(0);
    }, [diet]);

    return {
        presets: (
            <Label label="Choose a preset" error={null}>
                <SelectNumber name="" value={selectedPreset} onChange={onPresetChange}>
                    {diet.presets.map((preset, index) => (
                        <option key={index} value={index}>
                            {preset.name}
                        </option>
                    ))}
                </SelectNumber>
            </Label>
        ),
        form,
        onPresetChange,
        settings: <forms.Fields form={form} />,
        results: {
            bmr: round(getBMR(profile)),
            tdee: round(getTDEE(profile)),
            targets,
            assignments: (diet.assignments ?? [0, 0, 0, 0, 0, 0, 0]).map((ref) => ({
                reference: ref,
            })),
        },
    };
};

export function Results(props: {result: DietResult; useKilojoules: boolean}) {
    const {useKilojoules, result} = props;

    return (
        <>
            <typography.Table>
                <tbody>
                    <tr>
                        <th>BMR</th>
                        <td>{result.bmr}</td>
                    </tr>
                    <tr>
                        <th>TDEE</th>
                        <td>{result.tdee}</td>
                    </tr>
                </tbody>
            </typography.Table>
            <typography.Table>
                <thead>
                    <tr className="text-left">
                        {result.targets.length > 1 && <th>Day</th>}
                        <th>{useKilojoules ? "Kj" : "Calories"}</th>
                        <th>Protein</th>
                        <th>Fat</th>
                        <th>Carbs</th>
                    </tr>
                </thead>
                <tbody>
                    {result.targets.map((target, index) => {
                        return (
                            <tr key={index}>
                                {result.targets.length > 1 && <th>{target.name}</th>}
                                <td>{target.calories_target}</td>
                                <td>{target.protein_target}</td>
                                <td>{target.fat_target}</td>
                                <td>{target.carbs_target}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </typography.Table>
        </>
    );
}

export const getGeneralDay = ({
    settings,
    profile,
}: {
    settings: server.journal.nutrition.GeneralCalculatorForm.values;
    profile: Profile;
}) => {
    const tdee = getTDEE(profile);
    const modifier = parseFloat(settings.variance!) / 100;
    const calories = tdee * (1 + modifier);
    const weight = parseFloat(profile.weight!);

    const protein = weight * parseFloat(settings.protein_multiplier!);
    const remainingCalories = calories - protein * 4;
    const macroSplit = parseFloat(settings.fat_carbs!);

    return {
        name: "Macros",
        calories,
        protein,
        fat: (remainingCalories * macroSplit) / 9,
        carbs: (remainingCalories * (1 - macroSplit)) / 4,
    };
};

export const general = {
    presets: [
        {
            name: "Lose weight",
            variance: "-20",
            protein_multiplier: "1.25",
            fat_carbs: "0.50",
        },
        {
            name: "Slowly gain weight",
            variance: "10",
            protein_multiplier: "1.25",
            fat_carbs: "0.50",
        },
        {
            name: "Quickly gain weight",
            variance: "20",
            protein_multiplier: "1.25",
            fat_carbs: "0.50",
        },
        {
            name: "Maintain weight",
            variance: "0",
            protein_multiplier: "1.25",
            fat_carbs: "0.50",
        },
    ],
    results: [getGeneralDay],
};

const getWorkoutDay = ({
    settings,
    profile,
}: {
    settings: server.journal.nutrition.LeangainsCalculatorForm.values;
    profile: Profile;
}) => {
    const tdee = getTDEE(profile);
    const modifier = parseFloat(settings.workout_day_variance!) / 100;
    const calories = tdee * (1 + modifier);

    const protein =
        parseFloat(profile.weight!) * parseFloat(settings.protein_multiplier!);
    const remainingCalories = calories - protein * 4;
    const macroSplit = parseFloat(settings.workout_day_fat!);

    return {
        name: "Workout day",
        calories,
        protein,
        fat: (remainingCalories * macroSplit) / 9,
        carbs: (remainingCalories * (1 - macroSplit)) / 4,
    };
};

const getRestDay = ({
    settings,
    profile,
}: {
    settings: server.journal.nutrition.LeangainsCalculatorForm.values;
    profile: Profile;
}) => {
    const tdee = getTDEE(profile);
    const modifier = parseFloat(settings.rest_day_variance ?? "-30") / 100;
    const calories = tdee * (1 + modifier);

    const protein =
        parseFloat(profile.weight ?? "81") *
        parseFloat(settings.protein_multiplier ?? "2.2");
    const remainingCalories = calories - protein * 4;
    const macroSplit = parseFloat(settings.rest_day_fat!);

    return {
        name: "Rest day",
        calories,
        protein,
        fat: (remainingCalories * macroSplit) / 9,
        carbs: (remainingCalories * (1 - macroSplit)) / 4,
        useNetCarbs: true,
    };
};

const getMaintenanceDay = ({
    settings,
    profile,
}: {
    settings: server.journal.nutrition.LeangainsCalculatorForm.values;
    profile: Profile;
}) => {
    const tdee = getTDEE(profile);
    // const modifier = parseFloat(settings.workout_day_variance || '0') / 100;
    const modifier = 0; // For maintenance day, no modifier.
    const calories = tdee * (1 + modifier);

    const protein =
        parseFloat(profile.weight ?? "81") *
        parseFloat(settings.protein_multiplier ?? "2.2");
    const remainingCalories = calories - protein * 4;
    const macroSplit = 0.5;

    return {
        name: "Maintenance day",
        calories,
        protein,
        fat: (remainingCalories * macroSplit) / 9,
        carbs: (remainingCalories * (1 - macroSplit)) / 4,
    };
};

export const leangains = {
    presets: [
        {
            name: "Cut",
            workout_day_variance: "0",
            rest_day_variance: "-30",
            workout_day_fat: "0.25",
            rest_day_fat: "0.75",
            protein_multiplier: "2.5",
        },
        {
            name: "Slow bulk",
            workout_day_variance: "40",
            rest_day_variance: "-10",
            workout_day_fat: "0.25",
            rest_day_fat: "0.75",
            protein_multiplier: "2.5",
        },
        {
            name: "Recomp",
            workout_day_variance: "20",
            rest_day_variance: "-20",
            workout_day_fat: "0.25",
            rest_day_fat: "0.75",
            protein_multiplier: "2.5",
        },
    ],
    results: [getWorkoutDay, getRestDay, getMaintenanceDay],
    assignments: [0, 1, 0, 1, 0, 1, 1],
};

const getKetoDay = ({
    settings,
    profile,
}: {
    settings: server.journal.nutrition.KetoCalculatorForm.values;
    profile: Profile;
}) => {
    const tdee = getTDEE(profile);
    const modifier = parseFloat(settings.variance!) / 100;
    const calories = tdee * (1 + modifier);
    const carbs = parseFloat(settings.carb_limit!);
    const remainingCalories = calories - carbs * 4;
    const protein = (remainingCalories * 0.25) / 4;
    const fat = (remainingCalories * 0.75) / 9;

    return {
        name: "Macros",
        calories,
        protein,
        fat,
        carbs,
        useNetCarbs: true,
    };
};

const keto = {
    presets: [
        {
            name: "Induction",
            variance: "-20",
            carb_limit: "20",
        },
        {
            name: "Lose weight",
            variance: "-20",
            carb_limit: "30",
        },
        {
            name: "Maintenance",
            variance: "0",
            carb_limit: "30",
        },
        {
            name: "Gain weight",
            variance: "20",
            carb_limit: "40",
        },
    ],
    results: [getKetoDay],
};

export const diets = {
    general,
    leangains,
    keto,
};
